/**
 * React hook for fetching blog analytics data from PostHog
 */
import { useState, useEffect } from 'react';
import posthogAnalytics from '../services/posthogAnalytics';

/**
 * Hook to fetch view count for a single blog post
 */
export const useBlogViewCount = (blogSlug) => {
  const [viewCount, setViewCount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let isMounted = true;

    const fetchViewCount = async () => {
      if (!blogSlug) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        
        const count = await posthogAnalytics.getBlogViewCount(blogSlug);
        
        if (isMounted) {
          setViewCount(count);
        }
      } catch (err) {
        if (isMounted) {
          setError(err.message);
          console.warn(`Failed to fetch view count for ${blogSlug}:`, err);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchViewCount();

    return () => {
      isMounted = false;
    };
  }, [blogSlug]);

  return { viewCount, loading, error };
};

/**
 * Hook to fetch view counts for all blog posts
 */
export const useAllBlogViewCounts = () => {
  const [viewCounts, setViewCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let isMounted = true;

    const fetchAllViewCounts = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const counts = await posthogAnalytics.getAllBlogViewCounts();
        
        if (isMounted) {
          setViewCounts(counts);
        }
      } catch (err) {
        if (isMounted) {
          setError(err.message);
          console.warn('Failed to fetch all blog view counts:', err);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchAllViewCounts();

    return () => {
      isMounted = false;
    };
  }, []);

  return { viewCounts, loading, error };
};

/**
 * Hook to get view count for a specific blog with fallback to static data
 */
export const useBlogViewCountWithFallback = (blogSlug, fallbackCount = 0) => {
  const { viewCount, loading, error } = useBlogViewCount(blogSlug);

  // Return real count if available, fallback count if error, or loading state
  const displayCount = error ? fallbackCount : (viewCount !== null ? viewCount : fallbackCount);
  
  return {
    viewCount: displayCount,
    loading,
    error,
    isRealData: viewCount !== null && !error
  };
};

/**
 * Hook for popular blog posts
 */
export const usePopularBlogPosts = (limit = 5) => {
  const [popularPosts, setPopularPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let isMounted = true;

    const fetchPopularPosts = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const posts = await posthogAnalytics.getPopularBlogPosts(limit);
        
        if (isMounted) {
          setPopularPosts(posts);
        }
      } catch (err) {
        if (isMounted) {
          setError(err.message);
          console.warn('Failed to fetch popular blog posts:', err);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchPopularPosts();

    return () => {
      isMounted = false;
    };
  }, [limit]);

  return { popularPosts, loading, error };
};